import { Button, message } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import { FoodSafetySurfaceCard } from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_READINESS_AREA,
  FOOD_SAFETY_READINESS_COPY,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  confirmFoodSafetySetupReadiness,
  fetchFoodSafetySetupReadiness,
} from 'src/utils/foodSafetyApi'
import { confirmWithFocusReturn } from 'src/utils/dialogFocus'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import {
  FOOD_SAFETY_READINESS_CONFIRM_LABEL,
  FOOD_SAFETY_READINESS_STAFF_LINK_HINT,
  FOOD_SAFETY_READINESS_STAFF_LINK_LABEL,
  formatReadinessAreaDetail,
  formatReadinessStatus,
  isReadinessStaffPreviewArea,
  readinessAreaIssueLines,
  readinessAreaSetupSection,
  readinessConfirmDialogLines,
  readinessStateLabel,
  readinessStateTone,
} from 'src/utils/foodSafetySetupReadiness'

/**
 * Manage Setup: each area's state, "See what staff see", and "Review &
 * confirm Food Safety setup". Hidden when Express can't answer (old build).
 * `refreshKey` changes after setup saves so states stay current.
 */
const FoodSafetySetupReadinessCard = ({
  venueId,
  refreshKey,
  visibleSections = [],
  onOpenSection,
  onOpenRecipients,
}) => {
  const [readiness, setReadiness] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const saveLock = useFoodSafetySaveLock()

  const load = useCallback(async () => {
    if (!venueId) return
    try {
      setReadiness(await fetchFoodSafetySetupReadiness(venueId))
    } catch (_error) {
      setReadiness(null)
    }
  }, [venueId])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  if (!readiness || !Array.isArray(readiness.areas)) return null

  const status = formatReadinessStatus(readiness)
  const staffPath = `/v/${venueId}/food-safety`
  const confirmed = status.tone === 'done'

  const confirm = () => {
    confirmWithFocusReturn({
      title: FOOD_SAFETY_READINESS_CONFIRM_LABEL,
      content: (
        <DialogBody data-testid='fs-readiness-confirm-body'>
          {readinessConfirmDialogLines(readiness).map(line => (
            <p key={line}>{line}</p>
          ))}
        </DialogBody>
      ),
      okText: 'Confirm setup',
      cancelText: 'Keep reviewing',
      onOk: () =>
        saveLock.run('confirm', async () => {
          setConfirming(true)
          try {
            let next
            try {
              next = await confirmFoodSafetySetupReadiness(venueId)
            } catch (error) {
              message.error(error?.message || 'Could not confirm setup.')
              return
            }
            message.success('Food Safety setup confirmed')
            if (next?.areas) {
              setReadiness(next)
              return
            }
            const refreshed = await refreshAfterFoodSafetySave(async () =>
              setReadiness(await fetchFoodSafetySetupReadiness(venueId)),
            )
            if (!refreshed) {
              message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
            }
          } finally {
            setConfirming(false)
          }
        }),
    })
  }

  return (
    <Card data-testid='fs-readiness' data-tone={status.tone}>
      <H3>Setup readiness</H3>
      <Status data-tone={status.tone} data-testid='fs-readiness-status'>
        <StatusHeadline>{status.headline}</StatusHeadline>
        {status.detail ? <StatusDetail>{status.detail}</StatusDetail> : null}
      </Status>

      <AreaList>
        {readiness.areas.map(area => {
          if (isReadinessStaffPreviewArea(area)) {
            return (
              <AreaRow
                key={area.key}
                data-testid={`fs-readiness-area-${area.key}`}
              >
                <AreaMain>
                  <AreaLabel>{area.label}</AreaLabel>
                  <AreaDetail>
                    {FOOD_SAFETY_READINESS_STAFF_LINK_HINT}
                  </AreaDetail>
                </AreaMain>
                <AreaLink to={staffPath} data-testid='fs-readiness-staff-link'>
                  {FOOD_SAFETY_READINESS_STAFF_LINK_LABEL}
                </AreaLink>
              </AreaRow>
            )
          }
          const section = readinessAreaSetupSection(area, visibleSections)
          const isRecipients =
            area.key === FOOD_SAFETY_READINESS_AREA.REPORT_RECIPIENTS
          const open = section
            ? () => onOpenSection?.(section)
            : isRecipients
            ? onOpenRecipients
            : null
          const detail = formatReadinessAreaDetail(area)
          const issues = readinessAreaIssueLines(area)
          return (
            <AreaRow
              key={area.key}
              data-testid={`fs-readiness-area-${area.key}`}
            >
              <AreaMain>
                {open ? (
                  <AreaButton type='button' onClick={open}>
                    {area.label}
                  </AreaButton>
                ) : (
                  <AreaLabel>{area.label}</AreaLabel>
                )}
                {detail ? <AreaDetail>{detail}</AreaDetail> : null}
                {issues.map(line => (
                  <AreaIssue key={line}>{line}</AreaIssue>
                ))}
                {area.changedSinceConfirmed ? (
                  <AreaChanged data-testid={`fs-readiness-changed-${area.key}`}>
                    Changed since confirmed
                  </AreaChanged>
                ) : null}
              </AreaMain>
              <StatePill
                data-tone={readinessStateTone(area.state)}
                data-testid={`fs-readiness-state-${area.key}`}
              >
                {readinessStateLabel(area)}
              </StatePill>
            </AreaRow>
          )
        })}
      </AreaList>

      <Actions>
        <Button
          type={confirmed ? 'default' : 'primary'}
          loading={confirming}
          onClick={confirm}
          data-testid='fs-readiness-confirm'
        >
          {FOOD_SAFETY_READINESS_CONFIRM_LABEL}
        </Button>
      </Actions>
      <Note data-testid='fs-readiness-note'>
        {readiness.summary?.responsibilityNote ||
          FOOD_SAFETY_READINESS_COPY.responsibility}
      </Note>
    </Card>
  )
}

export default FoodSafetySetupReadinessCard

const Card = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};

  h3 {
    margin: 0;
  }
`

const Status = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border-left: 3px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};

  &[data-tone='done'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }

  &[data-tone='changed'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff7ef;
  }
`

const StatusHeadline = styled.strong`
  font-size: 0.95rem;
`

const StatusDetail = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const AreaList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`

const AreaRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};

  &:first-child {
    border-top: none;
  }
`

const AreaMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`

const AreaLabel = styled.span`
  font-weight: 600;
`

const AreaButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colours.panSilver || '#ccc'};
`

const AreaDetail = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const AreaIssue = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
`

const AreaChanged = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #8a5a0a;
`

const AreaLink = styled(Link)`
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
`

const StatePill = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-tone='ok'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    color: #135200;
  }

  &[data-tone='attention'] {
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff2e8;
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }

  &[data-tone='todo'] {
    border-color: #e8c48a;
    color: #8a5a0a;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
`

const Note = styled(BodyText)`
  margin: 0;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const DialogBody = styled.div`
  p {
    margin: 0 0 0.5rem;
  }
`
